import React from "react";
import { Row, Col, Label, Input, Button, Spinner, Alert, Badge } from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { useParasutSettings } from "../hooks/useParasutSettings";

const NAVY = "#0A316E";

const STATUS_META = {
    PENDING: { label: "Bekliyor", color: "#B0B7C3" },
    SENT: { label: "Gönderildi", color: "#71C30B" },
    FAILED: { label: "Başarısız", color: "#dc3545" },
    CANCELLED: { label: "İptal", color: "#E07A7A" },
    SKIPPED: { label: "Atlandı", color: "#B0B7C3" },
};

const OPERATION_LABELS = {
    ADD_INVOICE: "Fatura",
    ADD_COLLECTION: "Tahsilat",
    CANCEL_INVOICE: "Fatura İptali",
    CANCEL_COLLECTION: "Tahsilat İptali",
};

const styles = `
  .parasut-modern .table thead tr { background-color: #f9f9f9; }
  .parasut-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
  .parasut-modern .table td { vertical-align: middle; }
  .parasut-modern .table-hover tbody tr:hover { background: #fcfcfd; }
  .parasut-modern .btn-rounded { border-radius: 999px; }
  .parasut-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
  .parasut-modern .btn-navy:hover { filter: brightness(1.1); }
  .parasut-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
`;

export default function ParasutSettingsTab({ companyId, otherActiveLabel, onSaved }) {
    const {
        loading, saving, connecting,
        settings, setSettings,
        accounts, accountsLoading,
        connect, save,
        queue, queueLoading, queueStatus, changeQueueStatus, loadQueue, retryingId, retryItem,
    } = useParasutSettings(companyId, onSaved);

    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner color="primary" />
                <div className="mt-2 text-muted">Paraşüt ayarları yükleniyor...</div>
            </div>
        );
    }

    return (
        <div className="parasut-modern">
            <style>{styles}</style>

            <p className="text-muted mb-4">
                Cari hareketleriniz Paraşüt'e otomatik aktarılır: borç/fatura → satış faturası, tahsilat → fatura ödemesi
                veya kasa/banka işlemi. Paraşüt hesabınızı bağlamak için aşağıdaki butonu kullanın.
            </p>

            {!settings.connected ? (
                <div className="text-center p-4 border rounded-4">
                    <i className="bx bx-link-alt fs-1 d-block mb-2" style={{ color: NAVY }}></i>
                    <p className="text-muted mb-3">Paraşüt hesabınız henüz bağlı değil.</p>
                    <Button className="btn-navy btn-rounded px-4" onClick={connect} disabled={connecting}>
                        {connecting ? <Spinner size="sm" /> : <i className="bx bx-link me-1"></i>}{" "}
                        Paraşüt'e Bağlan
                    </Button>
                </div>
            ) : (
                <>
                    <Alert color="success" className="border-0 shadow-sm d-flex align-items-center">
                        <i className="bx bx-check-circle me-2 fs-5"></i>
                        Paraşüt hesabınız bağlı.
                    </Alert>

                    {!settings.eInvoiceEnabled && (
                        <Alert color="info" className="border-0 shadow-sm">
                            <i className="bx bx-info-circle me-2"></i>
                            e-Fatura/e-Arşiv aktarımı bu sürümde kapalıdır; normal fatura kaydı oluşturulur.
                        </Alert>
                    )}

                    <Row className="g-3 mt-1">
                        <Col md={6}>
                            <Label className="fw-semibold">Nakit tahsilat hesabı (Kasa)</Label>
                            <Input
                                type="select"
                                className="pill-input"
                                value={settings.defaultCashAccountId || ""}
                                disabled={accountsLoading}
                                onChange={(e) => setSettings((s) => ({ ...s, defaultCashAccountId: e.target.value }))}
                            >
                                <option value="">Seçiniz...</option>
                                {accounts.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name} {a.currency ? `(${a.currency})` : ""}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={6}>
                            <Label className="fw-semibold">Havale/Kredi Kartı tahsilat hesabı (Banka)</Label>
                            <Input
                                type="select"
                                className="pill-input"
                                value={settings.defaultBankAccountId || ""}
                                disabled={accountsLoading}
                                onChange={(e) => setSettings((s) => ({ ...s, defaultBankAccountId: e.target.value }))}
                            >
                                <option value="">Seçiniz...</option>
                                {accounts.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name} {a.currency ? `(${a.currency})` : ""}
                                    </option>
                                ))}
                            </Input>
                        </Col>

                        <Col md={12} className="mt-3">
                            <div className="form-check form-switch">
                                <Input
                                    id="parasut-enabled"
                                    type="switch"
                                    role="switch"
                                    checked={settings.enabled}
                                    onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))}
                                />
                                <Label className="form-check-label" htmlFor="parasut-enabled">Entegrasyon aktif</Label>
                            </div>
                            {settings.enabled && otherActiveLabel && (
                                <div className="small mt-2" style={{ color: "#9a6700" }}>
                                    <i className="bx bx-info-circle me-1"></i>
                                    Kaydettiğinizde aktif olan <strong>{otherActiveLabel}</strong> entegrasyonu
                                    otomatik olarak pasifleştirilecek.
                                </div>
                            )}
                        </Col>
                    </Row>

                    <div className="mt-4">
                        <Button className="btn-navy btn-rounded px-4" onClick={() => save()} disabled={saving}>
                            {saving ? <Spinner size="sm" /> : <i className="bx bx-save me-1"></i>} Kaydet
                        </Button>
                    </div>

                    <hr className="my-4" />

                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                        <h5 className="mb-0 fw-bold" style={{ color: NAVY }}>
                            <i className="bx bx-transfer-alt me-2"></i>Senkron Kuyruğu
                        </h5>
                        <div className="d-flex gap-2 align-items-center">
                            <Input
                                type="select"
                                bsSize="sm"
                                className="pill-input"
                                style={{ width: 160 }}
                                value={queueStatus}
                                onChange={(e) => changeQueueStatus(e.target.value)}
                            >
                                <option value="">Tüm Durumlar</option>
                                <option value="PENDING">Bekliyor</option>
                                <option value="SENT">Gönderildi</option>
                                <option value="FAILED">Başarısız</option>
                                <option value="CANCELLED">İptal</option>
                                <option value="SKIPPED">Atlandı</option>
                            </Input>
                            <Button color="light" size="sm" className="btn-rounded" onClick={() => loadQueue(queue.page)} disabled={queueLoading}>
                                <i className="bx bx-refresh"></i>
                            </Button>
                        </div>
                    </div>

                    {queueLoading ? (
                        <div className="text-center p-4">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Kuyruk yükleniyor...</div>
                        </div>
                    ) : queue.content.length === 0 ? (
                        <div className="text-center p-4 text-muted">
                            <i className="bx bx-transfer-alt fs-2 d-block mb-2"></i>
                            Senkron kaydı bulunamadı. Yeni cari hareketler oluştukça burada listelenir.
                        </div>
                    ) : (
                        <>
                            <Table className="table table-hover">
                                <Thead>
                                    <Tr>
                                        <Th>İşlem</Th>
                                        <Th>Belge No</Th>
                                        <Th>Durum</Th>
                                        <Th>Deneme</Th>
                                        <Th>Hata</Th>
                                        <Th>Tarih</Th>
                                        <Th className="text-end">Aksiyon</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {queue.content.map((item) => {
                                        const meta = STATUS_META[item.status] || STATUS_META.PENDING;
                                        return (
                                            <Tr key={item.id}>
                                                <Td>{OPERATION_LABELS[item.operation] || item.operation}</Td>
                                                <Td className="font-monospace small">{item.documentNumber || "-"}</Td>
                                                <Td>
                                                    <Badge pill style={{ backgroundColor: meta.color, borderRadius: 999 }}>
                                                        {meta.label}
                                                    </Badge>
                                                </Td>
                                                <Td>{item.attempts}</Td>
                                                <Td className="small text-danger" style={{ maxWidth: 240 }} title={item.lastError || ""}>
                                                    {item.lastError
                                                        ? (item.lastError.length > 60 ? item.lastError.slice(0, 60) + "…" : item.lastError)
                                                        : "-"}
                                                </Td>
                                                <Td className="small text-muted">
                                                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString("tr-TR") : "-"}
                                                </Td>
                                                <Td className="text-end">
                                                    {item.status === "FAILED" && (
                                                        <Button size="sm" className="btn-navy btn-rounded" onClick={() => retryItem(item.id)} disabled={retryingId === item.id}>
                                                            {retryingId === item.id ? <Spinner size="sm" /> : (<><i className="bx bx-revision me-1"></i>Yeniden Dene</>)}
                                                        </Button>
                                                    )}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>

                            {queue.totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">Toplam {queue.totalElements} kayıt</small>
                                    <div className="d-flex gap-1">
                                        <Button size="sm" color="light" className="btn-rounded" disabled={queue.page === 0} onClick={() => loadQueue(queue.page - 1)}>
                                            <i className="bx bx-chevron-left"></i>
                                        </Button>
                                        <span className="align-self-center small text-muted px-2">{queue.page + 1} / {queue.totalPages}</span>
                                        <Button size="sm" color="light" className="btn-rounded" disabled={queue.page + 1 >= queue.totalPages} onClick={() => loadQueue(queue.page + 1)}>
                                            <i className="bx bx-chevron-right"></i>
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
